import type { SceneDef } from "../../genesis/schema";

/**
 * Le Parcours du Disciple — Le Baptême (jeu 1, scène 3).
 * Rendered entirely from data by the WorldManager (Genesis engine).
 *
 * Signature mechanics (Ac 2:38), une vraie rivière et une vraie assemblée :
 *  - Une rivière réelle qui traverse le plateau d'est en ouest, bordée de
 *    roseaux, de palmiers et de galets ; l'assemblée rassemblée sur la rive sud.
 *  - L'immersion : se tenir dans la rivière (lever_action, holdS 4) entouré
 *    de l'assemblée — le cœur de la scène (Ac 2:38).
 *  - 3 nouveaux croyants à conduire jusqu'à la Rive du rassemblement
 *    (guide_target) avant l'immersion.
 *  - 6 pierres du souvenir à ramasser dans le gué (pickup) pour dresser le
 *    Mémorial des pierres, comme au Jourdain (Jos 4:21-22).
 *  - La réjouissance : 5 corbeilles de fête (drag_drop) à apporter à la Table
 *    de la réjouissance (Ac 2:46), et la Corne de réjouissance (lever_action).
 *  - PNJ : Pierre l'apôtre (Ac 2:38), le voyageur éthiopien baptisé plein de
 *    joie (Ac 8:39), Lydie de Thyatire (Ac 16:14-15).
 *
 * Ancres bibliques : Ac 2:38-41 (repentance, baptême, trois mille âmes),
 * Rm 6:4 (ensevelis et ressuscités avec Christ), Jos 4:21-22 (les pierres du
 * mémorial), Ac 8:36-39 (l'Éthiopien), Ac 16:14-15 (Lydie), Ac 2:46 (la joie).
 * Cadrage d'espérance : le baptême est une fête de résurrection — lumière du
 * plein jour, chants, table dressée. Le Père n'est jamais figuré.
 */
export const parcoursDuDiscipleScene3: SceneDef = {
  id: "parcours-du-disciple/scene-3",
  game: "parcours-du-disciple",
  title: { fr: "Le Parcours du Disciple — Le Baptême", en: "The Disciple's Journey — The Baptism" },
  order: 3,
  keyName: { fr: "Clé de la Porte Étroite", en: "Key of the Narrow Gate" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 64,
    skyColor: "#a4d0e8",
    groundColor: "#7c9c54",
    lighting: "day",
    props: [
      // La rivière en amont (ouest) — le courant qui descend vers le gué.
      { id: "riviere_amont", shape: "water", pos: [-20, 0, -10], color: "#4c86bd", size: 1.4, scatter: 10, scatterRadius: 8 },
      // La rivière au centre — le gué peu profond où l'on descend pour l'immersion.
      { id: "riviere_gue", shape: "water", pos: [0, 0, -12], color: "#5a94c9", size: 1.3, scatter: 8, scatterRadius: 6 },
      // La rivière en aval (est) — le courant qui repart vers les collines.
      { id: "riviere_aval", shape: "water", pos: [20, 0, -11], color: "#4c86bd", size: 1.4, scatter: 10, scatterRadius: 8 },
      // L'assemblée rassemblée sur la rive sud, venue entourer les baptisés.
      { id: "assemblee_rive", shape: "person", pos: [-4, 0, 2], color: "#c8b088", size: 1.02, scatter: 22, scatterRadius: 10 },
      // Familles en fête sur la colline, chants et rires d'après-baptême.
      { id: "familles_fete", shape: "person", pos: [16, 0, 18], color: "#b9a274", size: 1, scatter: 8, scatterRadius: 6 },
      // Roseaux des berges, côté sud du gué.
      { id: "roseaux_berges", shape: "cone", pos: [-8, 0, -6], color: "#6a8f4f", size: 0.5, scatter: 16, scatterRadius: 12 },
      // Palmiers de la rive ouest.
      { id: "palmiers_rive", shape: "tree", pos: [-20, 0, 8], color: "#4f7a42", size: 1.8, scatter: 12, scatterRadius: 9 },
      // Oliviers du village, à l'est près des maisons.
      { id: "oliviers_village", shape: "tree", pos: [22, 0, 14], color: "#6d7d46", size: 1.4, scatter: 10, scatterRadius: 7 },
      // Galets clairs du gué, polis par le courant.
      { id: "galets_gue", shape: "sphere", pos: [2, 0, -16], color: "#9aa0a8", size: 0.3, scatter: 14, scatterRadius: 6 },
      // Maisons du village voisin, d'où arrivent les nouveaux croyants.
      { id: "maisons_village", shape: "box", pos: [26, 0, 2], color: "#b09a72", size: 2.2, scatter: 6, scatterRadius: 8 },
      // Collines du Jourdain à l'horizon nord.
      { id: "collines_jourdain", shape: "mountain", pos: [0, 0, -28], color: "#8a9a6f", size: 3, scatter: 5, scatterRadius: 10 },
      // Lampes de fête suspendues au-dessus de la table de la réjouissance (y > 0).
      { id: "lampes_fete", shape: "flame", pos: [14, 2.5, 9], color: "#ffd27a", size: 0.35, scatter: 10, scatterRadius: 7 },
    ],
  },
  spawn: [0, 0, 20],
  difficulty: {
    // goalCount scale sur le VRAI total d'interactables avançants :
    // 6 pickup (pierres) + 5 drag_drop (corbeilles) + 3 guide_target
    // + 2 lever_action = 16.
    D: { goalCount: 5, hints: true },
    A: { goalCount: 11, hints: true },
    E: { goalCount: 16, hints: false, timerS: 450 },
  },
  interactables: [
    // ——— L'immersion dans la rivière (Ac 2:38) — le cœur de la scène ———
    {
      id: "immersion_bapteme", type: "lever_action",
      label: { fr: "L'Immersion dans la rivière (Actes 2:38)", en: "The Immersion in the river (Acts 2:38)" },
      pos: [0, 0, -10], shape: "water", color: "#2f6fae", size: 2.8, holdS: 4,
    },
    // ——— La Rive du rassemblement : y conduire les nouveaux croyants ———
    {
      id: "rive_attente", type: "build_slot",
      label: { fr: "La Rive du rassemblement", en: "The Bank of gathering" },
      pos: [-6, 0, -4], shape: "gate", color: "#dfe8f0", size: 2.6, targetId: "rive_attente",
    },
    {
      id: "croyant_village", type: "guide_target",
      label: { fr: "Nouveau croyant venu du village", en: "New believer come from the village" },
      pos: [-16, 0, 10], shape: "person", color: "#a8b890", size: 1, targetId: "rive_attente", count: 1,
    },
    {
      id: "croyante_famille", type: "guide_target",
      label: { fr: "Nouvelle croyante avec sa famille (Actes 16:15)", en: "New believer with her household (Acts 16:15)" },
      pos: [12, 0, 12], shape: "person", color: "#c0a0a8", size: 1, targetId: "rive_attente", count: 1,
    },
    {
      id: "croyant_questions", type: "guide_target",
      label: { fr: "Jeune croyant plein de questions", en: "Young believer full of questions" },
      pos: [18, 0, -2], shape: "person", color: "#90a8b8", size: 1, targetId: "rive_attente", count: 1,
    },
    // ——— Le Mémorial des pierres (Jos 4:21-22) : 6 pierres du gué ———
    {
      id: "memorial_pierres", type: "build_slot",
      label: { fr: "Le Mémorial des pierres (Josué 4:21-22)", en: "The Memorial of stones (Joshua 4:21-22)" },
      pos: [8, 0, -16], shape: "stele", color: "#a99d84", size: 2, targetId: "memorial_pierres",
    },
    {
      id: "pierre_fidelite", type: "pickup",
      label: { fr: "Pierre du souvenir : la fidélité", en: "Stone of remembrance: faithfulness" },
      pos: [-4, 0, -8], shape: "box", color: "#9b9284", size: 0.55, targetId: "memorial_pierres", count: 1,
    },
    {
      id: "pierre_delivrance", type: "pickup",
      label: { fr: "Pierre du souvenir : la délivrance", en: "Stone of remembrance: deliverance" },
      pos: [4, 0, -7], shape: "box", color: "#a89b7f", size: 0.55, targetId: "memorial_pierres", count: 1,
    },
    {
      id: "pierre_promesse", type: "pickup",
      label: { fr: "Pierre du souvenir : la promesse", en: "Stone of remembrance: the promise" },
      pos: [-8, 0, -11], shape: "box", color: "#8f8878", size: 0.55, targetId: "memorial_pierres", count: 1,
    },
    {
      id: "pierre_grace", type: "pickup",
      label: { fr: "Pierre du souvenir : la grâce", en: "Stone of remembrance: grace" },
      pos: [7, 0, -12], shape: "box", color: "#b0a68e", size: 0.55, targetId: "memorial_pierres", count: 1,
    },
    {
      id: "pierre_alliance", type: "pickup",
      label: { fr: "Pierre du souvenir : l'alliance", en: "Stone of remembrance: the covenant" },
      pos: [-2, 0, -14], shape: "box", color: "#948a76", size: 0.55, targetId: "memorial_pierres", count: 1,
    },
    {
      id: "pierre_reconnaissance", type: "pickup",
      label: { fr: "Pierre du souvenir : la reconnaissance", en: "Stone of remembrance: gratitude" },
      pos: [3, 0, -18], shape: "box", color: "#a29a86", size: 0.55, targetId: "memorial_pierres", count: 1,
    },
    // ——— La Table de la réjouissance (Ac 2:46) : 5 corbeilles de fête ———
    {
      id: "table_rejouissance", type: "build_slot",
      label: { fr: "La Table de la réjouissance (Actes 2:46)", en: "The Table of rejoicing (Acts 2:46)" },
      pos: [14, 0, 8], shape: "box", color: "#a5713f", size: 2, targetId: "table_rejouissance",
    },
    {
      id: "corbeille_pains", type: "drag_drop",
      label: { fr: "Corbeille de pains", en: "Basket of loaves" },
      pos: [9, 0, 4], shape: "box", color: "#d8b26a", size: 0.5, targetId: "table_rejouissance", count: 1,
    },
    {
      id: "corbeille_fruits", type: "drag_drop",
      label: { fr: "Corbeille de fruits", en: "Basket of fruit" },
      pos: [18, 0, 12], shape: "sphere", color: "#c9713f", size: 0.5, targetId: "table_rejouissance", count: 1,
    },
    {
      id: "jarre_miel", type: "drag_drop",
      label: { fr: "Jarre de miel", en: "Jar of honey" },
      pos: [20, 0, 4], shape: "cylinder", color: "#c9a24b", size: 0.55, targetId: "table_rejouissance", count: 1,
    },
    {
      id: "outre_lait", type: "drag_drop",
      label: { fr: "Outre de lait", en: "Skin of milk" },
      pos: [11, 0, 15], shape: "capsule", color: "#e8e2d4", size: 0.55, targetId: "table_rejouissance", count: 1,
    },
    {
      id: "panier_figues", type: "drag_drop",
      label: { fr: "Panier de figues", en: "Basket of figs" },
      pos: [24, 0, 9], shape: "sphere", color: "#8a6b4a", size: 0.5, targetId: "table_rejouissance", count: 1,
    },
    // ——— La Corne de réjouissance : sonner la joie de l'assemblée ———
    {
      id: "corne_rejouissance", type: "lever_action",
      label: { fr: "La Corne de réjouissance", en: "The Horn of rejoicing" },
      pos: [-14, 0, 2], shape: "cone", color: "#c9a24b", size: 1.3, holdS: 2,
    },
    // ——— PNJ nommés ———
    {
      id: "pierre_apotre", type: "npc_dialog",
      label: { fr: "Pierre, l'apôtre de la Pentecôte (Actes 2:38)", en: "Peter, the apostle of Pentecost (Acts 2:38)" },
      pos: [-3, 0, 6], shape: "person", color: "#6b8ba0", size: 1.1,
    },
    {
      id: "voyageur_ethiopien", type: "npc_dialog",
      label: { fr: "Le voyageur éthiopien, reparti plein de joie (Actes 8:39)", en: "The Ethiopian traveler, gone on his way rejoicing (Acts 8:39)" },
      pos: [-20, 0, -8], shape: "person", color: "#7a6248", size: 1.1,
    },
    {
      id: "lydie", type: "npc_dialog",
      label: { fr: "Lydie de Thyatire, baptisée avec sa famille (Actes 16:14-15)", en: "Lydia of Thyatira, baptized with her household (Acts 16:14-15)" },
      pos: [-12, 0, 14], shape: "person", color: "#9a5a7a", size: 1.1,
    },
    // ——— Panneaux bibliques ———
    {
      id: "panel_ac238", type: "panel",
      label: { fr: "Repentez-vous et soyez baptisés", en: "Repent and be baptized" },
      pos: [4, 0, 10], shape: "stele", color: "#7fa0b5", size: 1.3, panelId: "p_ac238_bapteme",
    },
    {
      id: "panel_rm6", type: "panel",
      label: { fr: "Ensevelis et ressuscités avec Christ", en: "Buried and raised with Christ" },
      pos: [-9, 0, -14], shape: "stele", color: "#b9a06a", size: 1.3, panelId: "p_rm6_nouveaute",
    },
  ],
  objectives: [
    {
      id: "obj_immersion", kind: "reach",
      label: { fr: "Conduis les 3 nouveaux croyants à la rive, puis tiens-toi dans la rivière pour l'immersion", en: "Lead the 3 new believers to the bank, then stand in the river for the immersion" },
      target: "immersion_bapteme",
    },
    {
      id: "obj_memorial", kind: "build",
      label: { fr: "Dresse les 6 pierres du souvenir en mémorial sur la rive (Josué 4)", en: "Set the 6 stones of remembrance as a memorial on the bank (Joshua 4)" },
      target: "memorial_pierres", count: 6,
    },
  ],
  panels: [
    {
      id: "p_ac238_bapteme",
      title: { fr: "Repentez-vous et soyez baptisés", en: "Repent and be baptized" },
      reference: "Actes 2:38",
      passage: {
        fr: "Pierre leur dit: Repentez-vous, et que chacun de vous soit baptisé au nom de Jésus-Christ, pour le pardon de vos péchés; et vous recevrez le don du Saint-Esprit.",
        en: "Peter said to them, \"Repent, and be baptized, every one of you, in the name of Jesus Christ for the forgiveness of sins, and you will receive the gift of the Holy Spirit.\"",
      },
      explain: {
        fr: "Le jour de la Pentecôte, la foule touchée demande : « Que ferons-nous ? » Pierre répond par un chemin simple : se repentir, être baptisé, recevoir le don du Saint-Esprit. Le baptême n'est pas un exploit privé : ce jour-là, environ trois mille personnes descendent dans l'eau, entourées de l'assemblée — comme sur cette rive.",
        en: "On the day of Pentecost, the crowd, cut to the heart, asks: \"What shall we do?\" Peter answers with a simple way: repent, be baptized, receive the gift of the Holy Spirit. Baptism is not a private feat: that day about three thousand people go down into the water, surrounded by the assembly — as on this bank.",
      },
      didYouKnow: {
        fr: "Actes 2:41 précise qu'« environ trois mille âmes » furent ajoutées ce jour-là : le baptême a toujours été une fête d'accueil dans une famille, pas une cérémonie solitaire.",
        en: "Acts 2:41 notes that \"about three thousand souls\" were added that day: baptism has always been a feast of welcome into a family, not a solitary ceremony.",
      },
      application: {
        fr: "Qui pourrais-tu entourer, encourager ou simplement accompagner jusqu'à « la rive » dans sa démarche vers Dieu ?",
        en: "Whom could you surround, encourage, or simply walk with to \"the bank\" in their journey toward God?",
      },
    },
    {
      id: "p_rm6_nouveaute",
      title: { fr: "Marcher en nouveauté de vie", en: "Walk in newness of life" },
      reference: "Romains 6:4",
      passage: {
        fr: "Nous avons donc été ensevelis avec lui par le baptême en sa mort, afin que, comme Christ est ressuscité des morts par la gloire du Père, de même nous aussi nous marchions en nouveauté de vie.",
        en: "We were buried therefore with him through baptism into death, that just as Christ was raised from the dead through the glory of the Father, so we also might walk in newness of life.",
      },
      explain: {
        fr: "Paul explique le geste de l'immersion : descendre dans l'eau, c'est être enseveli avec Christ ; en remonter, c'est se lever avec lui pour une vie nouvelle. C'est pourquoi la rivière de cette scène est un lieu de fête et non de tristesse : on y descend pour ressusciter. Les pierres du mémorial garderont la mémoire de ce passage, comme Israël au Jourdain.",
        en: "Paul explains the gesture of immersion: going down into the water is being buried with Christ; coming up is rising with him into a new life. That is why the river of this scene is a place of celebration and not of sorrow: we go down in order to rise. The memorial stones will keep the memory of this crossing, like Israel at the Jordan.",
      },
      didYouKnow: {
        fr: "Après la traversée du Jourdain, douze pierres furent dressées pour que les enfants demandent un jour : « Que signifient ces pierres ? » (Josué 4:21-22) — chaque baptême est une histoire à raconter.",
        en: "After the crossing of the Jordan, twelve stones were set up so that children would one day ask: \"What do these stones mean?\" (Joshua 4:21-22) — every baptism is a story to tell.",
      },
      application: {
        fr: "Quelle « pierre du souvenir » pourrais-tu poser cette semaine — un mot, une photo, une note — pour te rappeler ce que Dieu a déjà fait dans ta vie ?",
        en: "What \"stone of remembrance\" could you set up this week — a word, a photo, a note — to remember what God has already done in your life?",
      },
    },
  ],
  quizzes: [
    {
      id: "q_pierre_pentecote", level: "D",
      prompt: { fr: "Le jour de la Pentecôte, que demande Pierre à la foule (Actes 2:38) ?", en: "On the day of Pentecost, what does Peter ask the crowd (Acts 2:38)?" },
      choices: [
        { fr: "Repentez-vous, et que chacun soit baptisé", en: "Repent, and let each one be baptized" },
        { fr: "Construisez un grand temple", en: "Build a great temple" },
        { fr: "Retournez chacun chez vous", en: "Each return to your own home" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Repentez-vous, et que chacun de vous soit baptisé au nom de Jésus-Christ » (Actes 2:38). Le chemin d'entrée est simple et ouvert à tous.",
        en: "\"Repent, and be baptized, every one of you, in the name of Jesus Christ\" (Acts 2:38). The way in is simple and open to all.",
      },
    },
    {
      id: "q_don_esprit", level: "A",
      prompt: { fr: "Selon Actes 2:38, que reçoit celui qui se repent et est baptisé ?", en: "According to Acts 2:38, what does the one who repents and is baptized receive?" },
      choices: [
        { fr: "Une place d'honneur dans l'assemblée", en: "A seat of honor in the assembly" },
        { fr: "Le don du Saint-Esprit", en: "The gift of the Holy Spirit" },
        { fr: "Une pierre du Jourdain", en: "A stone from the Jordan" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« …et vous recevrez le don du Saint-Esprit » (Actes 2:38). Dieu lui-même vient habiter et conduire le nouveau disciple.",
        en: "\"…and you will receive the gift of the Holy Spirit\" (Acts 2:38). God himself comes to dwell in and lead the new disciple.",
      },
    },
    {
      id: "q_trois_mille", level: "E",
      prompt: { fr: "D'après Actes 2:41, environ combien de personnes furent baptisées le jour de la Pentecôte ?", en: "According to Acts 2:41, about how many people were baptized on the day of Pentecost?" },
      choices: [
        { fr: "Cent vingt", en: "One hundred twenty" },
        { fr: "Environ trois mille", en: "About three thousand" },
        { fr: "Douze", en: "Twelve" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Ceux qui acceptèrent sa parole furent baptisés ; et, en ce jour-là, le nombre des disciples s'augmenta d'environ trois mille âmes » (Actes 2:41). Cent vingt, c'est le groupe d'avant la Pentecôte (Actes 1:15) ; douze, les apôtres.",
        en: "\"Those who received his word were baptized, and there were added that day about three thousand souls\" (Acts 2:41). One hundred twenty was the group before Pentecost (Acts 1:15); twelve, the apostles.",
      },
    },
    {
      id: "qD2", level: "D",
      prompt: { fr: "Que fit le voyageur éthiopien après son baptême (Actes 8:39) ?", en: "What did the Ethiopian traveler do after his baptism (Acts 8:39)?" },
      choices: [
        { fr: "Il poursuivit son chemin tout joyeux", en: "He went on his way rejoicing" },
        { fr: "Il retourna consulter les prêtres à Jérusalem", en: "He went back to consult the priests in Jerusalem" },
        { fr: "Il resta plusieurs jours au bord de l'eau", en: "He stayed by the water for several days" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« L'eunuque ne le vit plus. Tandis que, joyeux, il poursuivait sa route » (Actes 8:39). Le baptême n'est pas une fin : c'est un départ dans la joie.",
        en: "\"The eunuch didn't see him any more, for he went on his way rejoicing\" (Acts 8:39). Baptism is not an ending: it is a joyful setting out.",
      },
    },
    {
      id: "qD3", level: "D",
      prompt: { fr: "Avec qui Lydie fut-elle baptisée à Philippes (Actes 16:15) ?", en: "With whom was Lydia baptized at Philippi (Acts 16:15)?" },
      choices: [
        { fr: "Avec les apôtres seulement", en: "With the apostles only" },
        { fr: "Avec sa famille (sa maison)", en: "With her household" },
        { fr: "Avec toute la ville de Thyatire", en: "With the whole city of Thyatira" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Lorsqu'elle eut été baptisée, avec sa famille… » (Actes 16:15). La foi de Lydie entraîne toute sa maison — le baptême est une fête qui rassemble.",
        en: "\"When she and her household were baptized…\" (Acts 16:15). Lydia's faith draws in her whole household — baptism is a feast that gathers.",
      },
    },
    {
      id: "qD4", level: "D",
      prompt: { fr: "À quoi servent les pierres dressées après la traversée du Jourdain (Josué 4:21-22) ?", en: "What are the stones set up after crossing the Jordan for (Joshua 4:21-22)?" },
      choices: [
        { fr: "À bâtir un pont sur la rivière", en: "To build a bridge over the river" },
        { fr: "À marquer la frontière du pays", en: "To mark the border of the land" },
        { fr: "À rappeler ce que Dieu a fait, quand les enfants demanderont ce qu'elles signifient", en: "To recall what God did, when the children ask what they mean" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Lorsque vos enfants demanderont un jour à leurs pères : Que signifient ces pierres ? vous en instruirez vos enfants » (Josué 4:21-22). Chaque pierre du souvenir raconte une œuvre de Dieu.",
        en: "\"When your children ask their fathers in time to come, saying, 'What do these stones mean?' then you shall let your children know\" (Joshua 4:21-22). Every stone of remembrance tells a work of God.",
      },
    },
    {
      id: "qD5", level: "D",
      prompt: { fr: "Selon Actes 2:38, au nom de qui chacun doit-il être baptisé ?", en: "According to Acts 2:38, in whose name must each one be baptized?" },
      choices: [
        { fr: "Au nom de Pierre l'apôtre", en: "In the name of Peter the apostle" },
        { fr: "Au nom de Jésus-Christ", en: "In the name of Jesus Christ" },
        { fr: "Au nom de l'assemblée", en: "In the name of the assembly" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Que chacun de vous soit baptisé au nom de Jésus-Christ, pour le pardon de vos péchés » (Actes 2:38). Le baptême attache le disciple à une personne : Jésus-Christ.",
        en: "\"Be baptized, every one of you, in the name of Jesus Christ for the forgiveness of sins\" (Acts 2:38). Baptism binds the disciple to a person: Jesus Christ.",
      },
    },
    {
      id: "qA2", level: "A",
      prompt: { fr: "Selon Romains 6:4, que raconte le geste de descendre dans l'eau puis d'en remonter ?", en: "According to Romans 6:4, what does going down into the water and coming up again tell?" },
      choices: [
        { fr: "Une purification à refaire chaque année", en: "A purification to repeat every year" },
        { fr: "Être enseveli avec Christ, puis marcher avec lui en nouveauté de vie", en: "Being buried with Christ, then walking with him in newness of life" },
        { fr: "Un simple adieu à son village", en: "A simple farewell to one's village" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Nous avons été ensevelis avec lui par le baptême en sa mort… de même nous aussi nous marchions en nouveauté de vie » (Romains 6:4). Descendre, c'est mourir avec Christ ; remonter, c'est se lever avec lui.",
        en: "\"We were buried therefore with him through baptism into death… so we also might walk in newness of life\" (Romans 6:4). Going down is dying with Christ; coming up is rising with him.",
      },
    },
    {
      id: "qA3", level: "A",
      prompt: { fr: "Qui expliqua l'Écriture au voyageur éthiopien avant de le baptiser (Actes 8:35) ?", en: "Who explained the Scripture to the Ethiopian traveler before baptizing him (Acts 8:35)?" },
      choices: [
        { fr: "Philippe", en: "Philip" },
        { fr: "Pierre", en: "Peter" },
        { fr: "Barnabas", en: "Barnabas" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Alors Philippe, ouvrant la bouche et commençant par ce passage, lui annonça la bonne nouvelle de Jésus » (Actes 8:35), puis il le baptisa dans l'eau rencontrée en chemin (Actes 8:38).",
        en: "\"Philip opened his mouth, and beginning from this Scripture, preached to him Jesus\" (Acts 8:35), then baptized him in the water they found along the road (Acts 8:38).",
      },
    },
    {
      id: "qA4", level: "A",
      prompt: { fr: "Quel était le métier de Lydie de Thyatire (Actes 16:14) ?", en: "What was the trade of Lydia of Thyatira (Acts 16:14)?" },
      choices: [
        { fr: "Elle tissait des voiles de bateau", en: "She wove ship sails" },
        { fr: "Elle tenait un puits sur la route de Philippes", en: "She kept a well on the road to Philippi" },
        { fr: "Elle était marchande de pourpre", en: "She was a seller of purple" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Une femme craignant Dieu, nommée Lydie, marchande de pourpre, de la ville de Thyatire » (Actes 16:14). Le Seigneur lui ouvrit le cœur pour écouter ce que disait Paul.",
        en: "\"A certain woman named Lydia, a seller of purple, of the city of Thyatira, one who worshiped God\" (Acts 16:14). The Lord opened her heart to listen to what Paul said.",
      },
    },
    {
      id: "qA5", level: "A",
      prompt: { fr: "Selon Actes 2:41, que firent d'abord ceux qui furent baptisés le jour de la Pentecôte ?", en: "According to Acts 2:41, what did those baptized on Pentecost do first?" },
      choices: [
        { fr: "Ils acceptèrent la parole annoncée par Pierre", en: "They received the word Peter preached" },
        { fr: "Ils dressèrent douze pierres au bord de l'eau", en: "They set up twelve stones by the water" },
        { fr: "Ils partirent aussitôt en voyage", en: "They immediately set out on a journey" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Ceux qui acceptèrent sa parole furent baptisés » (Actes 2:41). L'écoute et l'accueil de la Parole précèdent la descente dans l'eau : c'est l'ordre du chemin.",
        en: "\"Those who received his word were baptized\" (Acts 2:41). Hearing and receiving the Word comes before going down into the water: that is the order of the way.",
      },
    },
    {
      id: "qE2", level: "E",
      prompt: { fr: "Combien de pierres furent retirées du Jourdain pour dresser le mémorial (Josué 4) ?", en: "How many stones were taken from the Jordan to set up the memorial (Joshua 4)?" },
      choices: [
        { fr: "Six, comme dans ce gué", en: "Six, as in this ford" },
        { fr: "Douze, selon le nombre des tribus d'Israël", en: "Twelve, according to the number of the tribes of Israel" },
        { fr: "Quarante, une par année au désert", en: "Forty, one for each year in the wilderness" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Douze pierres furent dressées « selon le nombre des tribus des enfants d'Israël » (Josué 4:8) à Guilgal. Les 6 pierres de cette rive en sont l'écho — le principe demeure : se souvenir et raconter.",
        en: "Twelve stones were set up \"according to the number of the tribes of the children of Israel\" (Joshua 4:8) at Gilgal. The 6 stones on this bank echo them — the principle stands: remember and tell.",
      },
    },
    {
      id: "qE3", level: "E",
      prompt: { fr: "Selon Romains 6:4, par quoi Christ est-il ressuscité des morts ?", en: "According to Romans 6:4, through what was Christ raised from the dead?" },
      choices: [
        { fr: "Par la puissance des apôtres", en: "Through the power of the apostles" },
        { fr: "Par les eaux du Jourdain", en: "Through the waters of the Jordan" },
        { fr: "Par la gloire du Père", en: "Through the glory of the Father" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Comme Christ est ressuscité des morts par la gloire du Père, de même nous aussi nous marchions en nouveauté de vie » (Romains 6:4). La même gloire qui a relevé Christ relève le baptisé.",
        en: "\"Just as Christ was raised from the dead through the glory of the Father, so we also might walk in newness of life\" (Romans 6:4). The same glory that raised Christ raises the baptized.",
      },
    },
    {
      id: "qE4", level: "E",
      prompt: { fr: "Que dit l'Éthiopien à Philippe en apercevant de l'eau sur la route (Actes 8:36) ?", en: "What did the Ethiopian say to Philip on seeing water along the road (Acts 8:36)?" },
      choices: [
        { fr: "« Voici de l'eau ; qu'est-ce qui empêche que je sois baptisé ? »", en: "\"Behold, here is water. What is keeping me from being baptized?\"" },
        { fr: "« Attendons d'être arrivés à Jérusalem »", en: "\"Let us wait until we reach Jerusalem\"" },
        { fr: "« Cette eau est trop peu profonde pour un baptême »", en: "\"This water is too shallow for a baptism\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Voici de l'eau ; qu'est-ce qui empêche que je sois baptisé ? » (Actes 8:36). Quand le cœur a compris l'Écriture, il ne cherche pas de délai : Philippe et lui descendirent tous deux dans l'eau (Actes 8:38).",
        en: "\"Behold, here is water. What is keeping me from being baptized?\" (Acts 8:36). When the heart has understood the Scripture, it seeks no delay: both went down into the water (Acts 8:38).",
      },
    },
    {
      id: "qD6", level: "D",
      prompt: { fr: "Où Paul et ses compagnons rencontrent-ils Lydie (Actes 16:13-14) ?", en: "Where do Paul and his companions meet Lydia (Acts 16:13-14)?" },
      choices: [
        { fr: "Au marché de Thyatire", en: "At the market of Thyatira" },
        { fr: "Dans la synagogue d'Athènes", en: "In the synagogue of Athens" },
        { fr: "Au bord de la rivière, près de Philippes, dans un lieu de prière", en: "By the riverside near Philippi, at a place of prayer" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Le jour du sabbat, nous nous rendîmes, hors de la porte, vers une rivière, où nous pensions que se trouvait un lieu de prière » (Actes 16:13). C'est là, au bord de l'eau, que le Seigneur ouvre le cœur de Lydie — les rives sont souvent des lieux de commencement.",
        en: "\"On the Sabbath day we went forth outside of the city by a riverside, where we supposed there was a place of prayer\" (Acts 16:13). There, by the water, the Lord opens Lydia's heart — riverbanks are often places of beginnings.",
      },
    },
    {
      id: "qD7", level: "D",
      prompt: { fr: "Par qui et dans quelle rivière Jésus lui-même fut-il baptisé (Matthieu 3:13) ?", en: "By whom and in which river was Jesus himself baptized (Matthew 3:13)?" },
      choices: [
        { fr: "Par Jean-Baptiste, dans le Jourdain", en: "By John the Baptist, in the Jordan" },
        { fr: "Par Pierre, dans le lac de Galilée", en: "By Peter, in the lake of Galilee" },
        { fr: "Par André, dans une source de Nazareth", en: "By Andrew, in a spring at Nazareth" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Alors Jésus vint de la Galilée au Jourdain vers Jean, pour être baptisé par lui » (Matthieu 3:13). Le Maître descend le premier dans la rivière : chaque baptême marche dans ses pas.",
        en: "\"Then Jesus came from Galilee to the Jordan to John, to be baptized by him\" (Matthew 3:13). The Master goes down into the river first: every baptism walks in his steps.",
      },
    },
    {
      id: "qD8", level: "D",
      prompt: { fr: "Quelle charge le voyageur éthiopien exerçait-il dans son pays (Actes 8:27) ?", en: "What office did the Ethiopian traveler hold in his country (Acts 8:27)?" },
      choices: [
        { fr: "Capitaine de la garde royale", en: "Captain of the royal guard" },
        { fr: "Ministre de Candace, reine d'Éthiopie, chargé de tous ses trésors", en: "Officer of Candace, queen of the Ethiopians, over all her treasure" },
        { fr: "Scribe du temple de Jérusalem", en: "Scribe of the temple in Jerusalem" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Un eunuque, ministre de Candace, reine d'Éthiopie, et surintendant de tous ses trésors, venu à Jérusalem pour adorer » (Actes 8:27). Un haut dignitaire rentre chez lui avec un rouleau — et Dieu envoie Philippe sur sa route.",
        en: "\"A eunuch of great authority under Candace, queen of the Ethiopians, who was over all her treasure, who had come to Jerusalem to worship\" (Acts 8:27). A high official heads home with a scroll — and God sends Philip onto his road.",
      },
    },
    {
      id: "qD9", level: "D",
      prompt: { fr: "Dans quel lieu Josué dressa-t-il les douze pierres retirées du Jourdain (Josué 4:20) ?", en: "In what place did Joshua set up the twelve stones taken from the Jordan (Joshua 4:20)?" },
      choices: [
        { fr: "À Jéricho", en: "At Jericho" },
        { fr: "À Sichem", en: "At Shechem" },
        { fr: "À Guilgal", en: "At Gilgal" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Josué dressa à Guilgal les douze pierres qu'ils avaient prises du Jourdain » (Josué 4:20). Guilgal, premier campement en terre promise, devient le lieu-mémoire : chaque passage de l'eau mérite son monument.",
        en: "\"Joshua set up those twelve stones, which they took out of the Jordan, in Gilgal\" (Joshua 4:20). Gilgal, the first camp in the promised land, becomes the place of memory: every water crossing deserves its monument.",
      },
    },
    {
      id: "qA6", level: "A",
      prompt: { fr: "Que se passa-t-il quand Jésus remonta de l'eau après son baptême (Matthieu 3:16-17) ?", en: "What happened when Jesus came up out of the water after his baptism (Matthew 3:16-17)?" },
      choices: [
        { fr: "Les cieux s'ouvrirent, l'Esprit descendit comme une colombe, et une voix dit : « Celui-ci est mon Fils bien-aimé »", en: "The heavens opened, the Spirit descended like a dove, and a voice said: \"This is my beloved Son\"" },
        { fr: "La foule se dispersa en silence", en: "The crowd scattered in silence" },
        { fr: "Le Jourdain s'arrêta de couler", en: "The Jordan stopped flowing" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Dès que Jésus eut été baptisé, il sortit de l'eau. Et voici, les cieux s'ouvrirent, et il vit l'Esprit de Dieu descendre comme une colombe… Et voici, une voix fit entendre des cieux ces paroles: Celui-ci est mon Fils bien-aimé » (Matthieu 3:16-17). Le baptême est le lieu où l'amour du ciel se déclare.",
        en: "\"Jesus, when he was baptized, went up directly from the water: and behold, the heavens were opened to him. He saw the Spirit of God descending as a dove… Behold, a voice out of the heavens said, 'This is my beloved Son'\" (Matthew 3:16-17). Baptism is where heaven's love speaks out.",
      },
    },
    {
      id: "qA7", level: "A",
      prompt: { fr: "Selon Actes 16:14, qui ouvrit le cœur de Lydie pour qu'elle soit attentive aux paroles de Paul ?", en: "According to Acts 16:14, who opened Lydia's heart to listen to Paul's words?" },
      choices: [
        { fr: "La communauté de Philippes", en: "The community of Philippi" },
        { fr: "Paul, par la force de ses arguments", en: "Paul, by the strength of his arguments" },
        { fr: "Le Seigneur", en: "The Lord" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Le Seigneur lui ouvrit le cœur, pour qu'elle fût attentive à ce que disait Paul » (Actes 16:14). Avant toute descente dans l'eau, c'est Dieu qui ouvre : l'évangéliste parle, mais le Seigneur convertit.",
        en: "\"…whose heart the Lord opened to listen to the things which were spoken by Paul\" (Acts 16:14). Before anyone goes down into the water, it is God who opens: the evangelist speaks, but the Lord converts.",
      },
    },
    {
      id: "qA8", level: "A",
      prompt: { fr: "Qui envoya Philippe sur le chemin désert de Jérusalem à Gaza (Actes 8:26) ?", en: "Who sent Philip onto the desert road from Jerusalem to Gaza (Acts 8:26)?" },
      choices: [
        { fr: "Les apôtres réunis à Jérusalem", en: "The apostles gathered in Jerusalem" },
        { fr: "Un ange du Seigneur", en: "An angel of the Lord" },
        { fr: "Le voyageur éthiopien lui-même", en: "The Ethiopian traveler himself" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Un ange du Seigneur, s'adressant à Philippe, lui dit: Lève-toi, et va du côté du midi, sur le chemin qui descend de Jérusalem à Gaza » (Actes 8:26). Puis l'Esprit dit : « Avance, et approche-toi de ce char » (Actes 8:29). Les rencontres du baptême sont conduites pas à pas.",
        en: "\"An angel of the Lord spoke to Philip, saying, 'Arise, and go toward the south to the way that goes down from Jerusalem to Gaza'\" (Acts 8:26). Then the Spirit said: \"Go near, and join yourself to this chariot\" (Acts 8:29). Baptism encounters are led step by step.",
      },
    },
    {
      id: "qE5", level: "E",
      prompt: { fr: "Selon Josué 3:15-16, à quel moment les eaux du Jourdain s'arrêtèrent-elles ?", en: "According to Joshua 3:15-16, at what moment did the waters of the Jordan stop?" },
      choices: [
        { fr: "Dès que les pieds des sacrificateurs portant l'arche se mouillèrent au bord de l'eau", en: "As soon as the feet of the priests bearing the ark dipped in the edge of the water" },
        { fr: "Après sept jours de marche autour du fleuve", en: "After seven days of marching around the river" },
        { fr: "Quand tout le peuple eut fini de traverser", en: "When all the people had finished crossing" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Dès que les sacrificateurs qui portaient l'arche eurent trempé leurs pieds au bord de l'eau — le Jourdain regorge par-dessus toutes ses rives tout le temps de la moisson — les eaux… s'arrêtèrent » (Josué 3:15-16). Il fallut d'abord poser le pied dans l'eau : la foi précède le passage.",
        en: "\"When… the feet of the priests who bore the ark had dipped in the edge of the water (for the Jordan overflows all its banks all the time of harvest), the waters… stood\" (Joshua 3:15-16). The foot had to enter the water first: faith precedes the crossing.",
      },
    },
    {
      id: "qE6", level: "E",
      prompt: { fr: "Que fit Lydie aussitôt après son baptême (Actes 16:15) ?", en: "What did Lydia do right after her baptism (Acts 16:15)?" },
      choices: [
        { fr: "Elle vendit son commerce de pourpre", en: "She sold her purple trade" },
        { fr: "Elle insista pour accueillir Paul et ses compagnons dans sa maison", en: "She insisted on welcoming Paul and his companions into her house" },
        { fr: "Elle partit pour Jérusalem", en: "She left for Jerusalem" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Lorsqu'elle eut été baptisée, avec sa famille, elle nous fit cette demande: Si vous me jugez fidèle au Seigneur, entrez dans ma maison, et demeurez-y; et elle nous pressa par ses instances » (Actes 16:15). Le premier fruit de son baptême est une porte ouverte : l'hospitalité.",
        en: "\"When she and her household were baptized, she begged us, saying, 'If you have judged me to be faithful to the Lord, come into my house and stay.' So she persuaded us\" (Acts 16:15). The first fruit of her baptism is an open door: hospitality.",
      },
    },
  ],
  tips: [
    { fr: "Commence par conduire les 3 nouveaux croyants (du village, de la famille de Lydie, du chemin) jusqu'à la Rive du rassemblement : personne ne descend seul dans l'eau.", en: "Start by leading the 3 new believers (from the village, from Lydia's household, from the road) to the Bank of gathering: no one goes down into the water alone." },
    { fr: "Ramasse les 6 pierres du souvenir dans le gué et dresse le Mémorial sur la rive : « Que signifient ces pierres ? » (Josué 4:21-22).", en: "Pick up the 6 stones of remembrance in the ford and set up the Memorial on the bank: \"What do these stones mean?\" (Joshua 4:21-22)." },
    { fr: "Après l'immersion, apporte les 5 corbeilles à la Table de la réjouissance et fais sonner la Corne : l'Éthiopien « poursuivit sa route avec joie » (Actes 8:39).", en: "After the immersion, bring the 5 baskets to the Table of rejoicing and sound the Horn: the Ethiopian \"went on his way rejoicing\" (Acts 8:39)." },
  ],
  lesson: {
    fr: "Repentez-vous, et que chacun de vous soit baptisé au nom de Jésus-Christ, pour le pardon de vos péchés; et vous recevrez le don du Saint-Esprit. (Actes 2:38)",
    en: "Repent, and be baptized, every one of you, in the name of Jesus Christ for the forgiveness of sins, and you will receive the gift of the Holy Spirit. (Acts 2:38)",
  },
  audio: { ambience: "river" },
  multiplayer: { mode: "coop", maxPlayers: 12 },
};

export default parcoursDuDiscipleScene3;
